import { apiLogin, setToken } from '../lib/api';
import { saveUserInfo } from '../lib/storage';
import type { AppStateManager } from '../app';

const SITE_KEY = '0x4AAAAAADOVaCQ5Ijio7h2w';
let themeListener: ((e: Event) => void) | null = null;

export function renderLoginPage(container: HTMLElement, app: AppStateManager): void {
  // 기존 테마 리스너 제거
  if (themeListener) { window.removeEventListener('kalpha-theme-changed', themeListener); themeListener = null; }
  container.innerHTML = `
    <div class="text-center mb-10 animate-fade-in-up">
      <div class="w-24 h-24 rounded-[28px] mx-auto mb-6 flex items-center justify-center shadow-xl" style="background: linear-gradient(135deg, #2d6a4f, #1b4332);">
        <svg width="52" height="52" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="lbubble" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#74c69d"/>
              <stop offset="100%" stop-color="#40916c"/>
            </linearGradient>
          </defs>
          <path d="M28 32C28 26.5 32.5 22 38 22h24c5.5 0 10 4.5 10 10v20c0 5.5-4.5 10-10 10H48l-10 10 2-10h-2c-5.5 0-10-4.5-10-10V32z" fill="url(#lbubble)"/>
          <text x="50" y="50" font-family="system-ui, -apple-system, sans-serif" font-size="34" font-weight="800" fill="#ffffff" text-anchor="middle" dominant-baseline="middle" style="letter-spacing: -1px;">K</text>
          <circle cx="70" cy="28" r="5" fill="#ff6b6b"/>
        </svg>
      </div>
      <h1 class="text-3xl font-bold tracking-tight mb-1.5 text-primary">Kalphaotalk</h1>
      <p class="text-secondary text-sm font-medium">안전한 대화, 자유로운 연결</p>
    </div>
    <form id="login-form" class="space-y-4 animate-fade-in-up stagger-2">
      <div><label class="block text-sm font-semibold mb-1.5 ml-1 form-label">아이디</label><input id="login-username" type="text" autocomplete="username" required class="input-field w-full" placeholder="아이디를 입력하세요"></div>
      <div><label class="block text-sm font-semibold mb-1.5 ml-1 form-label">비밀번호</label>
        <div class="relative">
          <input id="login-password" type="password" autocomplete="current-password" required class="input-field w-full" placeholder="비밀번호를 입력하세요" style="padding-right:2.5rem">
          <button type="button" id="btn-toggle-pw" class="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary transition-colors p-1" aria-label="비밀번호 표시" title="비밀번호 표시">
            <svg id="icon-eye" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            <svg id="icon-eye-off" class="hidden" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
          </button>
        </div>
      </div>
      <div id="turnstile-login" class="flex justify-center"></div>
      <div id="login-error" class="error-text text-sm hidden px-1"></div>
      <button type="submit" class="btn btn-primary w-full py-3.5 mt-2 text-sm">로그인</button>
    </form>
    <div class="mt-8 text-center animate-fade-in-up stagger-3">
      <div class="relative"><div class="absolute inset-0 flex items-center"><div class="w-full border-t divider"></div></div><div class="relative flex justify-center text-xs"><span class="px-3 divider-text">또는</span></div></div>
      <p class="text-sm mt-5 text-secondary">계정이 없으신가요? <button id="btn-to-register" class="font-semibold link ml-1">회원가입</button></p>
    </div>`;

  // 비밀번호 표시/숨김 토글
  const pwInput = document.getElementById('login-password') as HTMLInputElement;
  const togglePwBtn = document.getElementById('btn-toggle-pw') as HTMLButtonElement;
  const iconEye = document.getElementById('icon-eye')!;
  const iconEyeOff = document.getElementById('icon-eye-off')!;
  togglePwBtn.addEventListener('click', () => {
    const isHidden = pwInput.type === 'password';
    pwInput.type = isHidden ? 'text' : 'password';
    iconEye.classList.toggle('hidden', !isHidden);
    iconEyeOff.classList.toggle('hidden', isHidden);
    togglePwBtn.setAttribute('aria-label', isHidden ? '비밀번호 숨기기' : '비밀번호 표시');
  });

  // Turnstile 렌더링
  let tsToken = '';
  let tsWidgetId: string | null = null;
  const tsContainer = document.getElementById('turnstile-login')!;
  function renderTurnstile(theme: string = document.documentElement.classList.contains('dark') ? 'dark' : 'light') {
    tsContainer.innerHTML = '';
    try {
      if ((window as any).turnstile) {
        tsWidgetId = (window as any).turnstile.render('#turnstile-login', {
          sitekey: SITE_KEY,
          theme,
          callback: (token: string) => { tsToken = token; },
          'error-callback': () => {
            tsContainer.innerHTML = '<p class="text-sm text-red-500 text-center">보안 인증을 불러오지 못했습니다. 새로고침 해주세요.</p>';
          }
        });
      } else {
        tsContainer.innerHTML = '<p class="text-sm text-red-500 text-center">보안 인증 스크립트를 로드하지 못했습니다.</p>';
      }
    } catch {
      tsContainer.innerHTML = '<p class="text-sm text-red-500 text-center">보안 인증 초기화 오류</p>';
    }
  }
  renderTurnstile();

  // 다크모드 변경 시 Turnstile 동기화
  const onThemeChange = (e: Event) => {
    const theme = (e as CustomEvent).detail?.theme || (document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    if (tsWidgetId && (window as any).turnstile) {
      try { (window as any).turnstile.remove('#turnstile-login'); } catch {}
      tsWidgetId = null;
    }
    renderTurnstile(theme);
  };
  themeListener = onThemeChange;
  window.addEventListener('kalpha-theme-changed', onThemeChange);

  const form = document.getElementById('login-form') as HTMLFormElement;
  const err = document.getElementById('login-error')!;
  const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const u = (document.getElementById('login-username') as HTMLInputElement).value.trim();
    const p = (document.getElementById('login-password') as HTMLInputElement).value;
    if (!u || !p) { err.textContent = '아이디와 비밀번호를 입력해주세요.'; err.classList.remove('hidden'); return; }
    if (!tsToken) { err.textContent = '로봇 인증을 완료해주세요.'; err.classList.remove('hidden'); return; }
    btn.disabled = true; btn.textContent = '로그인 중...'; err.classList.add('hidden');
    try {
      const res = await apiLogin(u, p, tsToken);
      if (res.success && res.data) { setToken(res.data.token); saveUserInfo({ user_id: res.data.user_id, username: res.data.username, display_name: res.data.display_name }); app.navigateTo('rooms'); }
      else { err.textContent = res.error || '로그인 실패'; err.classList.remove('hidden'); tsToken = ''; try { (window as any).turnstile?.reset('#turnstile-login'); } catch {} }
    } catch { err.textContent = '서버 연결 실패'; err.classList.remove('hidden'); }
    finally { btn.disabled = false; btn.textContent = '로그인'; }
  });
  document.getElementById('btn-to-register')!.addEventListener('click', () => app.navigateTo('register'));
}
